import { useEffect, useRef, useState } from 'react';
import { App, Button, Checkbox, DatePicker, Flex, Input, Select, Space, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { formatStock } from '@app/shared';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { LinesGrid } from '@/shared/ui/LinesGrid';
import type { LinesGridColumn } from '@/shared/ui/LinesGrid';
import { INVALID_INPUTS_HINT, IntInput, InvalidInputsContext, MoneyInput, useInvalidInputs } from '@/shared/ui/inputs';
import { CategorySearchSelect } from '@/entities/category';
import type { CategoryDto } from '@/entities/category';
import { CounterpartySelect } from '@/entities/counterparty';
import {
  useCancelDocumentMutation,
  useCreateDocumentMutation,
  useDeleteDocumentMutation,
  useDocumentCashOrdersQuery,
  useDuplicateTemplateQuery,
  useGetDocumentQuery,
  usePostDocumentMutation,
  usePreviewDocumentMutation,
  useRestoreDocumentMutation,
  useReturnDocumentToDraftMutation,
  useReturnTemplateQuery,
  useUnpostDocumentMutation,
  useUpdateDocumentMutation,
} from '@/entities/document';
import type { DocumentCounterpartyRef, DocumentDto, LineWarningEntry, WriteOffReason } from '@/entities/document';
import { BatchSelect } from '@/entities/stock';
import { AttachmentsBlock } from '@/features/attachments';
import { PreviewConsequencesDialog, useSaveWithPreview } from '@/features/document/preview-consequences';
import { isVersionConflict } from '@/features/document/concurrent-edit';
import { UnsavedChangesDialog } from '@/shared/ui';
import { useWindowManager } from '@/shared/lib/windows';
import { useStoreContext } from '@/shared/lib/store-context';
import { ownerPath } from '@/shared/config/routes';
import { formatDate, todayInTz } from '@/shared/lib/date';
import { describeWarnings } from '@/shared/lib/warnings';
import { getErrorCode, getErrorDetails, translateApiError } from '@/shared/api/errorToMessage';
import type { ApiQueryError } from '@/shared/api/errorToMessage';
import type { EditorDocumentType } from './model';
import {
  amountOf,
  applyCategoryDefaults,
  archivedLinesMessage,
  cashOrderTargetType,
  emptyLine,
  fillFromStock,
  adoptLineIds,
  footerTotals,
  fromDocumentLine,
  fromDuplicateTemplate,
  fromReturnTemplate,
  inventoryDiff,
  isDocumentComplete,
  postedQtyByCategory,
  qtyOf,
  recalcMarkup,
  reuseKeys,
  shouldReseed,
  toLineInput,
  windowTitle,
  windowWarnings,
} from './model';
import type { EditorLine } from './model';

const PAYMENT_STATUS_LABEL: Record<string, string> = { UNPAID: 'не оплачено', PARTIAL: 'частично', PAID: 'оплачено' };
/** §7/§11.2: у возврата «оплачено» — то, чем он закрыт: зачтено в долги + возвращено деньгами. */
const RETURN_SETTLEMENT_LABEL: Record<string, string> = { UNPAID: 'не закрыт', PARTIAL: 'закрыт частично', PAID: 'закрыт' };
const CASH_ORDER_PREFIX: Record<'CASH_IN' | 'CASH_OUT', string> = { CASH_IN: 'ПКО', CASH_OUT: 'РКО' };
const CASH_ORDER_STATUS_LABEL: Record<string, string> = { DRAFT: 'черновик', POSTED: 'проведён', CANCELLED: 'отменён' };
const WRITE_OFF_REASON_OPTIONS: { value: WriteOffReason; label: string }[] = [
  { value: 'SPOILAGE', label: 'Порча' },
  { value: 'EXPIRED', label: 'Истёк срок' },
  { value: 'SHORTAGE', label: 'Недостача' },
  { value: 'DEFECT', label: 'Брак' },
  { value: 'OTHER', label: 'Другое' },
];

export interface DocumentWindowProps {
  type: EditorDocumentType;
  id?: string;
  /** §11.2/§11.3 «Создать возврат»: id проведённой продажи/прихода-основания для нового документа. */
  returnTemplateFrom?: string;
  /** §12.6 «Дублировать»: id документа-источника для нового документа. */
  copyOf?: string;
  /** Номер документа-источника для заголовка «Новый приход (копия 125/2026)». */
  copyOfLabel?: string;
  onClose: () => void;
}

export function DocumentWindow({ type, id, returnTemplateFrom, copyOf, copyOfLabel, onClose }: DocumentWindowProps) {
  const { message, modal } = App.useApp();
  const ctx = useStoreContext();
  const [documentId, setDocumentId] = useState(id);
  const gridApiRef = useRef<{ focusCell: (row: number, col: number) => void } | null>(null);

  const { data: doc, isFetching, refetch: refetchDoc } = useGetDocumentQuery(documentId ?? '', { skip: !documentId });
  const [createDocument, { isLoading: isCreating }] = useCreateDocumentMutation();
  const [updateDocument, { isLoading: isUpdating }] = useUpdateDocumentMutation();
  const [postDocument, { isLoading: isPosting }] = usePostDocumentMutation();
  const [unpostDocument, { isLoading: isUnposting }] = useUnpostDocumentMutation();
  const [cancelDocument, { isLoading: isCancelling }] = useCancelDocumentMutation();
  const [deleteDocument, { isLoading: isDeleting }] = useDeleteDocumentMutation();
  const [returnToDraft, { isLoading: isReturningToDraft }] = useReturnDocumentToDraftMutation();
  const [restoreDocument, { isLoading: isRestoring }] = useRestoreDocumentMutation();
  const [previewDocument] = usePreviewDocumentMutation();
  // «Провести»: от нажатия до ответа сервера, включая проверку предупреждений и вопрос владельцу.
  const [isPosting_, setIsPosting_] = useState(false);
  const { runWithPreview, pendingDiff, resolvePending, isRunningPreview } = useSaveWithPreview();
  const windows = useWindowManager();

  const [header, setHeader] = useState<{
    docDate: string;
    counterpartyId?: string;
    comment: string;
    baseDocumentId?: string;
  }>({
    docDate: todayInTz(ctx.timezone),
    comment: '',
  });
  const [lines, setLines] = useState<EditorLine[]>([emptyLine(1)]);
  // Название контрагента документа — для поля выбора: архивного контрагента в списке нет.
  const [counterpartyRef, setCounterpartyRef] = useState<DocumentCounterpartyRef | undefined>(undefined);
  const [dirty, setDirty] = useState(false);
  function seedFrom(d: DocumentDto) {
    setSeededDoc(d);
    setHeader({
      docDate: d.docDate.slice(0, 10),
      counterpartyId: d.counterpartyId ?? undefined,
      comment: d.comment ?? '',
      baseDocumentId: d.baseDocumentId ?? undefined,
    });
    setCounterpartyRef(d.counterparty ?? undefined);
    const withStock = !(d.type === 'INVENTORY' && d.status !== 'DRAFT');
    const readOnly = d.status === 'CANCELLED' || !!d.deletedAt;
    const seeded =
      d.lines.length > 0
        ? [...d.lines.map((l) => fromDocumentLine(l, withStock)), ...(readOnly ? [] : [emptyLine(d.lines.length + 1)])]
        : [emptyLine(1)];
    setLines((prev) => reuseKeys(prev, seeded));
    setDirty(false);
  }

  // Loaded doc → seed the editor (render-phase, not an effect — see React docs on adjusting state
  // when a prop changes). Пересев повторяется при каждом обновлении документа с сервера: после
  // проведения в строках появляется себестоимость, после чужих правок — новые суммы. Несохранённые
  // правки владельца при этом не затираются (см. `shouldReseed`).
  const [seededDoc, setSeededDoc] = useState<DocumentDto | undefined>(undefined);
  if (doc && doc !== seededDoc && shouldReseed(seededDoc, doc, dirty)) {
    seedFrom(doc);
  }

  /** §8.7 «Параллельная правка»: VERSION_CONFLICT предлагает открыть свежую версию (отбрасывая
   * локальные правки) вместо тупикового тоста; любая другая ошибка идёт через обычный toast. */
  function handleMutationError(e: unknown) {
    if (!isVersionConflict(e)) {
      message.error(describeError(e));
      return;
    }
    modal.confirm({
      title: 'Документ изменён в другом окне',
      content: 'Кто-то успел сохранить более новую версию. Открыть свежую версию? Ваши несохранённые изменения будут потеряны.',
      okText: 'Открыть свежую версию',
      cancelText: 'Отмена',
      onOk: async () => {
        const result = await refetchDoc();
        if (result.data) seedFrom(result.data);
      },
    });
  }

  // §11.2/§11.3 «Создать возврат»: новый документ (без id) открытый с основанием — подтягиваем
  // шаблон один раз, тем же render-phase-seed паттерном, что и для загруженного doc выше.
  const { data: returnTemplate } = useReturnTemplateQuery(returnTemplateFrom ?? '', { skip: !returnTemplateFrom || !!id });
  const [seededTemplateFrom, setSeededTemplateFrom] = useState<string | undefined>(undefined);
  if (returnTemplate && returnTemplateFrom && returnTemplateFrom !== seededTemplateFrom) {
    setSeededTemplateFrom(returnTemplateFrom);
    setHeader((h) => ({ ...h, counterpartyId: returnTemplate.counterpartyId ?? undefined, baseDocumentId: returnTemplate.baseDocumentId }));
    setCounterpartyRef(returnTemplate.counterparty ?? undefined);
    setLines(fromReturnTemplate(returnTemplate));
  }

  // §12.6 «Дублировать»: новый документ (без id) открытый с источником — тот же паттерн.
  const { data: duplicateTemplate } = useDuplicateTemplateQuery(copyOf ?? '', { skip: !copyOf || !!id });
  const [seededCopyOf, setSeededCopyOf] = useState<string | undefined>(undefined);
  if (duplicateTemplate && copyOf && copyOf !== seededCopyOf) {
    setSeededCopyOf(copyOf);
    setHeader((h) => ({ ...h, counterpartyId: duplicateTemplate.counterpartyId ?? undefined, comment: duplicateTemplate.comment ?? '' }));
    setCounterpartyRef(duplicateTemplate.counterparty ?? undefined);
    setLines(fromDuplicateTemplate(duplicateTemplate));
  }

  const status = doc?.status ?? 'DRAFT';
  const isNew = !documentId;
  // §8.1/§8.7: удалённый документ хранит прежний статус, но окно у него своё — только чтение и
  // «Восстановить»/«Дублировать»/«Закрыть».
  const isDeleted = !!doc?.deletedAt;
  // §8.3/§8.7: проведённый документ теперь тоже редактируется («Сохранить» — перепроведение с
  // предпросмотром последствий по §8.5); отменённый и удалённый остаются полностью read-only
  // (см. renderFooter).
  const isReadOnly = status === 'CANCELLED' || isDeleted;
  const busy =
    isCreating ||
    isUpdating ||
    isPosting ||
    isPosting_ ||
    isUnposting ||
    isCancelling ||
    isDeleting ||
    isReturningToDraft ||
    isRestoring ||
    isRunningPreview;
  const { data: cashOrders } = useDocumentCashOrdersQuery(documentId ?? '', {
    skip: !documentId || isDeleted || cashOrderTargetType(type) === null,
  });

  /** Текст отказа: для архивного товара — с номером строки и названием, для занятой заявки — с
   * подсказкой, что делать; остальное — общий словарь по коду ошибки. */
  function describeError(e: unknown): string {
    const code = getErrorCode(e as ApiQueryError);
    if (code === 'CATEGORY_ARCHIVED') return archivedLinesMessage(getErrorDetails(e), lines) ?? translateApiError(e);
    if (code === 'ORDER_REQUEST_ALREADY_CONVERTED') {
      return `${translateApiError(e)}. Нужен такой же товар в новой продаже — используйте «Дублировать»`;
    }
    return translateApiError(e);
  }

  function updateLine(key: string, patch: Partial<EditorLine>) {
    setDirty(true);
    setLines((prev) => prev.map((l) => (l.key === key ? recalcMarkup({ ...l, ...patch }) : l)));
  }

  function addRow() {
    setDirty(true);
    setLines((prev) => [...prev, emptyLine(prev.length + 1)]);
  }

  /** Выбор товара в строке — как `updateLine`, но если это была последняя (ранее пустая) строка,
   *  сразу добавляет новую пустую строку снизу, чтобы можно было продолжить без Enter/кнопки. */
  function selectCategory(line: EditorLine, rowIndex: number, category: CategoryDto) {
    setDirty(true);
    setLines((prev) => {
      const next = prev.map((l) => (l.key === line.key ? recalcMarkup(applyCategoryDefaults(l, category, type)) : l));
      if (rowIndex === prev.length - 1 && !line.categoryId) {
        return [...next, emptyLine(next.length + 1)];
      }
      return next;
    });
  }

  function removeRow(key: string) {
    setDirty(true);
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((l) => l.key !== key).map((l, i) => ({ ...l, lineNo: i + 1 }))));
  }

  function handleFillFromStock() {
    setDirty(true);
    setLines((prev) => fillFromStock(prev));
  }

  function buildPayload() {
    return {
      docDate: header.docDate,
      counterpartyId: header.counterpartyId,
      comment: header.comment || undefined,
      lines: lines.filter((l) => l.categoryId).map((l) => toLineInput(l, type)),
    };
  }

  async function ensureCreated(): Promise<string> {
    if (documentId) return documentId;
    const created = await createDocument({ type, baseDocumentId: header.baseDocumentId, copiedFromDocumentId: copyOf, ...buildPayload() }).unwrap();
    setLines((prev) => adoptLineIds(prev, created.lines));
    setDocumentId(created.id);
    setDirty(false);
    return created.id;
  }

  /** Кнопки при неверном поле недоступны; сюда можно прийти ещё из диалога «Сохранить изменения?». */
  function refuseInvalidInput(): boolean {
    if (canSave) return false;
    message.warning(INVALID_INPUTS_HINT);
    return true;
  }

  async function handleSaveDraft(close: boolean) {
    if (refuseInvalidInput()) return;
    try {
      if (!documentId) {
        await ensureCreated();
      } else {
        const updated = await updateDocument({ id: documentId, rowVersion: doc!.rowVersion, body: buildPayload() }).unwrap();
        setLines((prev) => adoptLineIds(prev, updated.lines));
        setDirty(false);
      }
      message.success('Черновик сохранён');
      if (close) onClose();
    } catch (e) {
      handleMutationError(e);
    }
  }

  /** Предупреждения словами, с названиями товаров из строк окна. Архивный товар сюда не входит:
   * это не предупреждение, а запрет, и у него своё сообщение. */
  function warningTexts(warnings: LineWarningEntry[]): string[] {
    return describeWarnings(
      warnings.filter((w) => w.code !== 'ARCHIVED_CATEGORY'),
      (lineNo) => lines.find((l) => l.lineNo === lineNo && l.categoryId)?.categoryName,
    );
  }

  /** §1, §5: система не запрещает, а предупреждает — и спрашивает до проведения, пока минус ещё
   * не записан. Отказ оставляет документ черновиком. */
  function confirmWarnings(texts: string[]): Promise<boolean> {
    return new Promise((resolve) => {
      modal.confirm({
        title: 'Провести с предупреждениями?',
        content: (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {texts.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        ),
        okText: 'Провести',
        cancelText: 'Не проводить',
        width: 560,
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  }

  async function handlePost(close: boolean) {
    if (isPosting_ || refuseInvalidInput()) return;
    setIsPosting_(true);
    try {
      const currentId = await ensureCreated();
      const rowVersion = documentId ? (doc?.rowVersion ?? 1) : 1;
      const payload = documentId ? buildPayload() : undefined;
      // Предпросмотр проводит документ «начерно» и откатывает: предупреждения известны заранее.
      const preview = await previewDocument({ id: currentId, body: { action: 'post', rowVersion, document: payload } }).unwrap();
      const texts = warningTexts(preview.warnings);
      if (texts.length > 0 && !(await confirmWarnings(texts))) {
        message.info('Документ не проведён и остаётся черновиком');
        return;
      }
      const result = await postDocument({ id: currentId, body: { rowVersion, document: payload } }).unwrap();
      // Себестоимость и сумму потери считает склад при проведении — показываем их сразу, из ответа.
      seedFrom(result.document);
      message.success(result.warnings.length > 0 ? 'Документ проведён с предупреждениями' : 'Документ проведён');
      if (close) onClose();
    } catch (e) {
      handleMutationError(e);
    } finally {
      setIsPosting_(false);
    }
  }

  /** §8.3/§8.5/§8.7: «Сохранить» проведённого документа — перепроведение с правкой строк/шапки,
   * с предпросмотром последствий перед сохранением. */
  async function handleSaveRepost(close: boolean) {
    if (!documentId || !doc || refuseInvalidInput()) return;
    const payload = buildPayload();
    let warnings: LineWarningEntry[] = [];
    try {
      const saved = await runWithPreview(
        documentId,
        'post',
        doc.rowVersion,
        async (stateVersion) => {
          const result = await postDocument({ id: documentId, body: { rowVersion: doc.rowVersion, document: payload, stateVersion } }).unwrap();
          warnings = result.warnings;
          // Сидируем локальный `lines`/`header` напрямую из ответа мутации — она гарантированно
          // свежая (перепроведённая формой на сервере), а кэш `useGetDocumentQuery` обновится
          // позже; до тех пор его устаревшую версию отсекает `shouldReseed`.
          seedFrom(result.document);
        },
        payload,
      );
      if (saved) {
        message.success(warnings.length > 0 ? 'Документ перепроведён с предупреждениями' : 'Документ перепроведён');
        if (close) onClose();
      }
    } catch (e) {
      handleMutationError(e);
    }
  }

  /** §8.5: снятие проведения/отмена/удаление проведённого документа — предпросмотр последствий
   * перед сохранением (не блокирует, только предупреждает). */
  async function handleUnpost() {
    if (!documentId || !doc) return;
    try {
      const saved = await runWithPreview(documentId, 'unpost', doc.rowVersion, (stateVersion) =>
        unpostDocument({ id: documentId, body: { rowVersion: doc.rowVersion, stateVersion } }).unwrap(),
      );
      if (saved) message.success('Проведение снято');
    } catch (e) {
      handleMutationError(e);
    }
  }

  function handleCancel() {
    if (!documentId || !doc) return;
    modal.confirm({
      title: 'Отменить документ?',
      onOk: async () => {
        try {
          const saved = await runWithPreview(documentId, 'cancel', doc.rowVersion, (stateVersion) =>
            cancelDocument({ id: documentId, body: { rowVersion: doc.rowVersion, stateVersion } }).unwrap(),
          );
          if (saved) message.success('Документ отменён');
        } catch (e) {
          handleMutationError(e);
        }
      },
    });
  }

  function handleDelete() {
    if (!documentId || !doc) return;
    modal.confirm({
      title: 'Удалить документ?',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const saved =
            status === 'POSTED'
              ? await runWithPreview(documentId, 'remove', doc.rowVersion, (stateVersion) =>
                  deleteDocument({ id: documentId, body: { rowVersion: doc.rowVersion, stateVersion } }).unwrap(),
                )
              : await deleteDocument({ id: documentId, body: { rowVersion: doc.rowVersion } })
                  .unwrap()
                  .then(() => true);
          if (saved) {
            message.success('Документ удалён');
            onClose();
          }
        } catch (e) {
          handleMutationError(e);
        }
      },
    });
  }

  async function handleReturnToDraft() {
    if (!documentId || !doc) return;
    try {
      const result = await returnToDraft({ id: documentId, rowVersion: doc.rowVersion }).unwrap();
      seedFrom(result.document);
      message.success('Документ возвращён в черновик');
    } catch (e) {
      handleMutationError(e);
    }
  }

  /** §8.1: удалённый проведённый возвращается черновиком — влияние на склад и долги появится
   * только после повторного проведения. */
  async function handleRestore() {
    if (!documentId || !doc) return;
    try {
      const result = await restoreDocument({ id: documentId, rowVersion: doc.rowVersion }).unwrap();
      seedFrom(result.document);
      message.success(result.document.status === 'DRAFT' ? 'Документ восстановлен как черновик — проведите его заново' : 'Документ восстановлен');
    } catch (e) {
      handleMutationError(e);
    }
  }

  const footer = footerTotals(type, lines, status === 'POSTED' && !isDeleted);
  const hasArchivedLine = lines.some((l) => l.categoryId && l.archived);
  // Предупреждения видны всё время, пока документ открыт: те, что пришли с сервера, и подсказка
  // о минусе по уже введённым количествам (до сохранения и проведения).
  const warningsView = isReadOnly ? { texts: [], lineNos: new Set<number>() } : windowWarnings(type, lines, seededDoc?.warnings ?? [], postedQtyByCategory(seededDoc));
  // Поле с неверным значением отдаёт наружу «пусто»: у необязательного поля («Оплачено», оптовая
  // цена) это выглядело бы как «не заполнено», и документ сохранился бы без введённого.
  const invalidInputs = useInvalidInputs();
  const canSave = !invalidInputs.hasInvalid;
  const canPost = canSave && isDocumentComplete(type, header, lines);

  // §1.8/§8.7: предупреждение о несохранённых изменениях не только при закрытии окна внутри
  // приложения, но и при закрытии вкладки/перезагрузке браузера целиком.
  useEffect(() => {
    if (!dirty) return;
    function handler(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  /** §8.7: «Закрытие с несохранёнными изменениями» — Сохранить(-черновик) / Не сохранять / Отмена. */
  function handleClose() {
    if (!dirty) {
      onClose();
      return;
    }
    setShowUnsavedDialog(true);
  }

  async function handleSaveAndClose() {
    setShowUnsavedDialog(false);
    if (status === 'DRAFT') {
      await handleSaveDraft(true);
    } else {
      await handleSaveRepost(true);
    }
  }

  const columns: LinesGridColumn<EditorLine>[] = buildColumns(type, updateLine, selectCategory, isReadOnly, () => windows.open({ kind: 'category' }));

  return (
    <>
      <PreviewConsequencesDialog diff={pendingDiff} warningTexts={pendingDiff ? warningTexts(pendingDiff.warnings) : []} onResolve={resolvePending} />
      <UnsavedChangesDialog
        open={showUnsavedDialog}
        saveLabel={status === 'DRAFT' ? 'Сохранить черновик' : 'Сохранить'}
        saving={busy}
        onSave={() => void handleSaveAndClose()}
        onDiscard={() => {
          setShowUnsavedDialog(false);
          onClose();
        }}
        onCancel={() => setShowUnsavedDialog(false)}
      />
      <FloatingWindow kind={`document:${type}`} title={windowTitle(type, doc, isNew, isNew ? copyOfLabel : undefined)} onClose={handleClose} footer={renderFooter()}>
      <InvalidInputsContext.Provider value={invalidInputs.api}>
      <Flex vertical gap={8}>
        <Flex gap={8} wrap>
          <Flex vertical style={{ minWidth: 140 }}>
            <Typography.Text style={{ fontSize: 12 }}>Дата</Typography.Text>
            <DatePicker
              value={dayjs(header.docDate)}
              format="DD.MM.YYYY"
              disabled={isReadOnly}
              onChange={(d) => {
                setDirty(true);
                setHeader((h) => ({ ...h, docDate: d ? d.format('YYYY-MM-DD') : h.docDate }));
              }}
            />
          </Flex>
          <Flex vertical style={{ minWidth: 220, flex: 1 }}>
            <Typography.Text style={{ fontSize: 12 }}>Контрагент</Typography.Text>
            <CounterpartySelect
              value={header.counterpartyId}
              current={counterpartyRef}
              disabled={isReadOnly}
              onChange={(v) => {
                setDirty(true);
                setHeader((h) => ({ ...h, counterpartyId: v }));
              }}
            />
          </Flex>
          <Flex vertical style={{ minWidth: 200, flex: 2 }}>
            <Typography.Text style={{ fontSize: 12 }}>Комментарий</Typography.Text>
            <Input
              value={header.comment}
              disabled={isReadOnly}
              onChange={(e) => {
                setDirty(true);
                setHeader((h) => ({ ...h, comment: e.target.value }));
              }}
            />
          </Flex>
        </Flex>

        {doc?.recalcPending ? <Tag color="warning">Пересчитывается — данные могут быть неактуальны</Tag> : null}

        {isDeleted ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Документ удалён и в учёте не участвует. «Восстановить» вернёт его {status === 'CANCELLED' ? 'отменённым' : 'черновиком'}.
          </Typography.Text>
        ) : null}
        {hasArchivedLine && !isReadOnly ? (
          <Typography.Text type="danger" style={{ fontSize: 12 }}>
            В выделенных строках товар в архиве — замените его или верните из архива, иначе документ не провести.
          </Typography.Text>
        ) : null}

        {type === 'INVENTORY' && !isReadOnly ? (
          <Flex>
            <Button size="small" onClick={handleFillFromStock}>
              Заполнить по остаткам
            </Button>
          </Flex>
        ) : null}

        <LinesGrid
          lines={lines}
          columns={columns}
          getRowKey={(l) => l.key}
          onAddRow={isReadOnly ? () => undefined : addRow}
          onRemoveRow={isReadOnly ? () => undefined : removeRow}
          removeButtonPosition="start"
          readOnly={isReadOnly}
          rowClassName={(l) => {
            if (!l.categoryId) return undefined;
            if (l.archived) return 'cell-error';
            return warningsView.lineNos.has(l.lineNo) ? 'cell-warn' : undefined;
          }}
          gridApiRef={gridApiRef}
        />

        {warningsView.texts.length > 0 ? (
          <Flex vertical>
            {warningsView.texts.map((text) => (
              <Typography.Text key={text} type="warning" style={{ fontSize: 12 }}>
                {text}
              </Typography.Text>
            ))}
          </Flex>
        ) : null}

        <Flex gap={16}>
          <Typography.Text style={{ fontSize: 12 }}>Позиций: {footer.positions}</Typography.Text>
          {footer.pcs !== null ? <Typography.Text style={{ fontSize: 12 }}>Штук: {footer.pcs}</Typography.Text> : null}
          <Typography.Text style={{ fontSize: 12 }} strong>
            {footer.amountLabel}: {footer.amount ?? 'появится после проведения'}
          </Typography.Text>
        </Flex>

        {renderPaymentBlock()}

        {invalidInputs.hasInvalid && !isReadOnly ? (
          <Typography.Text type="danger" style={{ fontSize: 12 }}>
            {INVALID_INPUTS_HINT}
          </Typography.Text>
        ) : null}

        <AttachmentsBlock documentId={documentId} readOnly={isReadOnly} />
      </Flex>
      </InvalidInputsContext.Provider>
      </FloatingWindow>
    </>
  );

  /** §7: кнопка «Создать приходный/расходный ордер» — по образцу «Создать возврат» (returnTemplateFrom):
   * открывает настоящее окно кассового ордера, предзаполненное этим документом как основанием
   * (сумма/контрагент/вид операции подтягивает само окно через cash-order-template). Доступна уже на
   * несохранённом черновике — как и «Создать возврат», сначала тихо сохраняет документ. */
  async function handleCreateCashOrder(targetType: 'CASH_IN' | 'CASH_OUT') {
    if (!header.counterpartyId || refuseInvalidInput()) return;
    const currentId = await ensureCreated();
    windows.open({ kind: 'document', type: targetType, baseDocumentId: currentId });
  }

  function renderPaymentBlock() {
    const targetType = cashOrderTargetType(type);
    if (!targetType || isDeleted) return null;
    const isReturn = type === 'RETURN_IN' || type === 'RETURN_OUT';
    const summaryLabel = isReturn ? 'Закрыто' : 'Оплачено';
    const statusLabel = (isReturn ? RETURN_SETTLEMENT_LABEL : PAYMENT_STATUS_LABEL)[doc?.paymentStatus ?? 'UNPAID'];
    const buttonLabel = targetType === 'CASH_IN' ? 'Создать приходный ордер' : 'Создать расходный ордер';
    return (
      <Flex vertical gap={4}>
        <Flex gap={12} align="center" wrap>
          {doc ? (
            <Typography.Text style={{ fontSize: 12 }}>
              {summaryLabel}: {doc.paidAmount} из {doc.totalAmount} ({statusLabel})
            </Typography.Text>
          ) : null}
          {!isReadOnly && header.counterpartyId ? (
            <Button size="small" onClick={() => void handleCreateCashOrder(targetType)}>
              {buttonLabel}
            </Button>
          ) : null}
        </Flex>
        {cashOrders && cashOrders.length > 0 ? (
          <Flex vertical gap={2}>
            {cashOrders.map((o) => (
              <Typography.Link key={o.cashOrderId} style={{ fontSize: 12 }} onClick={() => windows.open({ kind: 'document', type: o.type, id: o.cashOrderId })}>
                {formatDate(o.docDate)} · {CASH_ORDER_PREFIX[o.type]}-{o.number}/{o.numberYear} ({CASH_ORDER_STATUS_LABEL[o.status] ?? o.status}) · {o.amount}
              </Typography.Link>
            ))}
          </Flex>
        ) : null}
      </Flex>
    );
  }

  /** §12.6: «Дублировать» — открывает новое окно того же типа, предзаполненное из этого документа. */
  function handleDuplicate() {
    if (!documentId || !doc) return;
    windows.open({ kind: 'document', type, copyOf: documentId, copyOfLabel: `${doc.number}/${doc.numberYear}` });
  }

  function renderFooter() {
    if (isFetching && documentId) return <Typography.Text type="secondary">Загрузка…</Typography.Text>;

    if (isDeleted) {
      return (
        <Space wrap>
          <Button type="primary" loading={busy} onClick={() => void handleRestore()}>
            Восстановить
          </Button>
          <Button onClick={handleDuplicate}>Дублировать</Button>
          <Button onClick={handleClose}>Закрыть</Button>
        </Space>
      );
    }
    if (status === 'DRAFT') {
      return (
        <Space wrap>
          <Button type="primary" loading={busy} disabled={!canPost} onClick={() => void handlePost(true)}>
            Провести и закрыть
          </Button>
          <Button loading={busy} disabled={!canPost} onClick={() => void handlePost(false)}>
            Провести
          </Button>
          <Button loading={busy} disabled={!canSave} onClick={() => void handleSaveDraft(false)}>
            Сохранить черновик
          </Button>
          <Button danger disabled={!documentId} loading={busy} onClick={() => void handleDelete()}>
            Удалить
          </Button>
          <Button disabled={!documentId} onClick={handleDuplicate}>
            Дублировать
          </Button>
          <Button onClick={handleClose}>Закрыть</Button>
        </Space>
      );
    }
    if (status === 'POSTED') {
      return (
        <Space wrap>
          <Button type="primary" loading={busy} disabled={!canPost} onClick={() => void handleSaveRepost(true)}>
            Сохранить и закрыть
          </Button>
          <Button loading={busy} disabled={!canPost} onClick={() => void handleSaveRepost(false)}>
            Сохранить
          </Button>
          {(type === 'SALE' || type === 'PURCHASE') && documentId ? (
            <Button
              onClick={() => windows.open({ kind: 'document', type: type === 'SALE' ? 'RETURN_IN' : 'RETURN_OUT', returnTemplateFrom: documentId })}
            >
              Создать возврат
            </Button>
          ) : null}
          {documentId ? (
            <Button onClick={() => window.open(ownerPath(ctx.storeId, `/print/documents/${documentId}`), '_blank')}>Печать</Button>
          ) : null}
          <Button loading={busy} onClick={() => void handleUnpost()}>
            Снять проведение
          </Button>
          <Button loading={busy} onClick={() => void handleCancel()}>
            Отменить
          </Button>
          <Button danger loading={busy} onClick={() => void handleDelete()}>
            Удалить
          </Button>
          <Button onClick={handleDuplicate}>Дублировать</Button>
          <Button onClick={handleClose}>Закрыть</Button>
        </Space>
      );
    }
    // CANCELLED
    return (
      <Space wrap>
        <Button loading={busy} onClick={() => void handleReturnToDraft()}>
          Вернуть в черновик
        </Button>
        <Button onClick={handleDuplicate}>Дублировать</Button>
        <Button onClick={handleClose}>Закрыть</Button>
      </Space>
    );
  }
}

function buildColumns(
  type: EditorDocumentType,
  updateLine: (key: string, patch: Partial<EditorLine>) => void,
  selectCategory: (line: EditorLine, rowIndex: number, category: CategoryDto) => void,
  readOnly: boolean,
  onAddCategory: () => void,
): LinesGridColumn<EditorLine>[] {
  const categoryCol: LinesGridColumn<EditorLine> = {
    key: 'category',
    title: 'Товар',
    width: 240,
    // §5 «Курсор встаёт в поле «Коробок»» после выбора товара — не реализовано в этом проходе
    // (борьба с ref-в-рендере line из react-hooks/refs не стоила усилий здесь); Tab всё ещё
    // работает нативно.
    render: (line, rowIndex) => (
      <Flex gap={4} align="center">
        {readOnly ? null : (
          <Button
            type="primary"
            size="small"
            shape="circle"
            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', flex: '0 0 auto' }}
            tabIndex={-1}
            aria-label="Добавить категорию"
            icon={<PlusOutlined />}
            onClick={onAddCategory}
          />
        )}
        <CategorySearchSelect
          value={line.categoryId}
          current={line.categoryId && line.categoryName ? { id: line.categoryId, name: line.categoryName, isArchived: line.archived } : undefined}
          disabled={readOnly}
          onChange={(_id, category) => selectCategory(line, rowIndex, category)}
        />
      </Flex>
    ),
  };

  if (type === 'SALE') {
    return [
      categoryCol,
      {
        key: 'stock',
        title: 'Остаток',
        width: 140,
        render: (line) => <Typography.Text style={{ fontSize: 12 }}>{line.stock ? formatStock(line.stock) : '—'}</Typography.Text>,
      },
      {
        key: 'boxes',
        title: 'Коробок',
        width: 80,
        align: 'right',
        render: (line, rowIndex) => (
          <IntInput value={line.boxes} onChange={(v) => updateLine(line.key, { boxes: v })} navRow={rowIndex} navCol={1} disabled={readOnly} />
        ),
      },
      {
        key: 'pcsPerBox',
        title: 'Штук в коробке',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <IntInput value={line.pcsPerBox} onChange={(v) => updateLine(line.key, { pcsPerBox: v })} navRow={rowIndex} navCol={2} disabled={readOnly} />
        ),
      },
      { key: 'qty', title: 'Количество', width: 90, align: 'right', render: (line) => <span>{qtyOf(line)}</span> },
      {
        key: 'price',
        title: 'Цена',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <MoneyInput value={line.unitPrice} onChange={(v) => updateLine(line.key, { unitPrice: v })} navRow={rowIndex} navCol={3} disabled={readOnly} />
        ),
      },
      { key: 'amount', title: 'Сумма', width: 100, align: 'right', render: (line) => <span>{amountOf(line)}</span> },
      {
        key: 'cost',
        title: 'Себестоимость',
        width: 110,
        align: 'right',
        render: (line) => <span>{line.costAmount ?? '—'}{line.costIsProvisional ? ' (пред.)' : ''}</span>,
      },
    ];
  }

  if (type === 'PURCHASE') {
    return [
      categoryCol,
      {
        key: 'boxes',
        title: 'Коробок',
        width: 80,
        align: 'right',
        render: (line, rowIndex) => (
          <IntInput value={line.boxes} onChange={(v) => updateLine(line.key, { boxes: v })} navRow={rowIndex} navCol={1} disabled={readOnly} />
        ),
      },
      {
        key: 'pcsPerBox',
        title: 'Штук в коробке',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <IntInput value={line.pcsPerBox} onChange={(v) => updateLine(line.key, { pcsPerBox: v })} navRow={rowIndex} navCol={2} disabled={readOnly} />
        ),
      },
      { key: 'qty', title: 'Количество', width: 90, align: 'right', render: (line) => <span>{qtyOf(line)}</span> },
      {
        key: 'price',
        title: 'Цена за ед.',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <MoneyInput value={line.unitPrice} onChange={(v) => updateLine(line.key, { unitPrice: v, lastEdited: line.lastEdited })} navRow={rowIndex} navCol={3} disabled={readOnly} />
        ),
      },
      { key: 'amount', title: 'Сумма закупки', width: 100, align: 'right', render: (line) => <span>{amountOf(line)}</span> },
      {
        key: 'wholesale',
        title: 'Оптовая цена',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <MoneyInput
            value={line.wholesalePrice}
            onChange={(v) => updateLine(line.key, { wholesalePrice: v, lastEdited: 'wholesale' })}
            navRow={rowIndex}
            navCol={4}
            disabled={readOnly}
          />
        ),
      },
      {
        key: 'markup',
        title: '%',
        width: 70,
        align: 'right',
        render: (line, rowIndex) => (
          <MoneyInput
            value={line.markupPct}
            onChange={(v) => updateLine(line.key, { markupPct: v, lastEdited: 'markup' })}
            navRow={rowIndex}
            navCol={5}
            disabled={readOnly}
          />
        ),
      },
      {
        key: 'isNew',
        title: 'Новинка',
        width: 70,
        align: 'center',
        render: (line) => <Checkbox checked={line.isNew} disabled={readOnly} onChange={(e) => updateLine(line.key, { isNew: e.target.checked })} />,
      },
      {
        key: 'isPromo',
        title: 'Акция',
        width: 60,
        align: 'center',
        render: (line) => <Checkbox checked={line.isPromo} disabled={readOnly} onChange={(e) => updateLine(line.key, { isPromo: e.target.checked })} />,
      },
      {
        key: 'expiry',
        title: 'Срок годности',
        width: 130,
        render: (line) => (
          <DatePicker
            value={line.expiryDate ? dayjs(line.expiryDate) : null}
            format="DD.MM.YYYY"
            disabled={readOnly}
            onChange={(d) => updateLine(line.key, { expiryDate: d ? d.format('YYYY-MM-DD') : null })}
          />
        ),
      },
    ];
  }

  if (type === 'RETURN_IN' || type === 'RETURN_OUT') {
    return [
      categoryCol,
      {
        key: 'base',
        title: type === 'RETURN_IN' ? 'Продано' : 'Куплено',
        width: 90,
        align: 'right',
        render: (line) => <span>{line.soldQty ?? '—'}</span>,
      },
      { key: 'returned', title: 'Уже возвращено', width: 110, align: 'right', render: (line) => <span>{line.alreadyReturnedQty ?? 0}</span> },
      {
        key: 'boxes',
        title: 'Коробок',
        width: 80,
        align: 'right',
        render: (line, rowIndex) => (
          <IntInput value={line.boxes} onChange={(v) => updateLine(line.key, { boxes: v })} navRow={rowIndex} navCol={1} disabled={readOnly} />
        ),
      },
      {
        key: 'pcsPerBox',
        title: 'Штук в коробке',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <IntInput value={line.pcsPerBox} onChange={(v) => updateLine(line.key, { pcsPerBox: v })} navRow={rowIndex} navCol={2} disabled={readOnly} />
        ),
      },
      { key: 'qty', title: 'Количество', width: 90, align: 'right', render: (line) => <span>{qtyOf(line)}</span> },
      {
        key: 'price',
        title: 'Цена',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <MoneyInput value={line.unitPrice} onChange={(v) => updateLine(line.key, { unitPrice: v })} navRow={rowIndex} navCol={3} disabled={readOnly} />
        ),
      },
      { key: 'amount', title: 'Сумма', width: 100, align: 'right', render: (line) => <span>{amountOf(line)}</span> },
      {
        key: 'cost',
        title: 'Себестоимость',
        width: 110,
        align: 'right',
        render: (line) => <span>{line.costAmount ?? '—'}{line.costIsProvisional ? ' (пред.)' : ''}</span>,
      },
      {
        key: 'overBase',
        title: 'Сверх основания',
        width: 110,
        align: 'right',
        render: (line) => <span>{line.qtyOverBase ? line.qtyOverBase : '—'}</span>,
      },
    ];
  }

  if (type === 'INVENTORY') {
    return [
      categoryCol,
      {
        key: 'accountedSealed',
        title: 'Учёт К',
        width: 80,
        align: 'right',
        render: (line) => <span>{line.stock ? line.stock.sealedBoxes : '—'}</span>,
      },
      {
        key: 'accountedLoose',
        title: 'Учёт Р',
        width: 80,
        align: 'right',
        render: (line) => <span>{line.stock ? line.stock.loosePcs : '—'}</span>,
      },
      {
        key: 'countedBoxes',
        title: 'Факт коробок',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <IntInput value={line.countedBoxes ?? null} onChange={(v) => updateLine(line.key, { countedBoxes: v })} min={0} navRow={rowIndex} navCol={1} disabled={readOnly} />
        ),
      },
      {
        key: 'countedLoose',
        title: 'Факт россыпь',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <IntInput value={line.countedLoose ?? null} onChange={(v) => updateLine(line.key, { countedLoose: v })} min={0} navRow={rowIndex} navCol={2} disabled={readOnly} />
        ),
      },
      {
        key: 'diff',
        title: 'Разница, шт',
        width: 100,
        align: 'right',
        render: (line) => {
          const diff = inventoryDiff(line);
          if (diff === null) return <span>—</span>;
          return <span style={{ color: diff < 0 ? '#cf1322' : diff > 0 ? '#389e0d' : undefined }}>{diff > 0 ? `+${diff}` : diff}</span>;
        },
      },
    ];
  }

  // WRITE_OFF (§11.4): без контрагента и цены — «Сумма» появится после проведения (себестоимость).
  return [
    categoryCol,
    {
      key: 'stock',
      title: 'Остаток',
      width: 140,
      render: (line) => <Typography.Text style={{ fontSize: 12 }}>{line.stock ? formatStock(line.stock) : '—'}</Typography.Text>,
    },
    {
      key: 'boxes',
      title: 'Коробок',
      width: 80,
      align: 'right',
      render: (line, rowIndex) => (
        <IntInput value={line.boxes} onChange={(v) => updateLine(line.key, { boxes: v })} navRow={rowIndex} navCol={1} disabled={readOnly} />
      ),
    },
    {
      key: 'pcsPerBox',
      title: 'Штук в коробке',
      width: 100,
      align: 'right',
      render: (line, rowIndex) => (
        <IntInput value={line.pcsPerBox} onChange={(v) => updateLine(line.key, { pcsPerBox: v })} navRow={rowIndex} navCol={2} disabled={readOnly} />
      ),
    },
    { key: 'qty', title: 'Количество', width: 90, align: 'right', render: (line) => <span>{qtyOf(line)}</span> },
    {
      key: 'batch',
      title: 'Партия',
      width: 220,
      render: (line) => <BatchSelect categoryId={line.categoryId} value={line.batchId ?? undefined} disabled={readOnly} onChange={(v) => updateLine(line.key, { batchId: v ?? null })} />,
    },
    {
      key: 'reason',
      title: 'Причина',
      width: 140,
      render: (line) => (
        <Select<WriteOffReason>
          value={line.reason ?? undefined}
          disabled={readOnly}
          options={WRITE_OFF_REASON_OPTIONS}
          style={{ width: '100%' }}
          onChange={(v) => updateLine(line.key, { reason: v })}
        />
      ),
    },
    {
      key: 'cost',
      title: 'Себестоимость',
      width: 110,
      align: 'right',
      render: (line) => <span>{line.costAmount ?? '—'}</span>,
    },
  ];
}
